import { useState, useMemo } from "react";

const ORDER_KEY = "tplex.cockpit.entity_order";

const loadOrder = () => {
    try {
        const raw = localStorage.getItem(ORDER_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
};
const saveOrder = (ids) => {
    try { localStorage.setItem(ORDER_KEY, JSON.stringify(ids)); } catch { /* ignore */ }
};

/**
 * Wiederverwendbarer Drag-&-Drop-Reorder-Hook für eine Liste von Entitäten/Items.
 *
 * @param items   Array mit Objekten, die ein eindeutiges id-Feld besitzen
 * @param idKey   Key auf dem Item, der die ID hält (default "id")
 * @returns { ordered, handlers(item), reset, isReordered, dragId, overId }
 *
 * Das `handlers(item)`-Objekt wird mit `{...handlers(item)}` an das DOM-Element
 * angehängt. Es enthält draggable + alle DnD-Listener.
 */
export function useEntityOrder(items, idKey = "id") {
    const [order, setOrder] = useState(() => loadOrder());
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);

    const ordered = useMemo(() => {
        if (!items) return [];
        const known = new Set(order);
        const byId = Object.fromEntries(items.map((it) => [it[idKey], it]));
        const head = order.map((id) => byId[id]).filter(Boolean);
        const tail = items.filter((it) => !known.has(it[idKey]));
        return [...head, ...tail];
    }, [items, order, idKey]);

    const reorder = (fromId, toId) => {
        if (!fromId || fromId === toId) return;
        const ids = ordered.map((it) => it[idKey]);
        const fromIdx = ids.indexOf(fromId);
        const toIdx = ids.indexOf(toId);
        if (fromIdx < 0 || toIdx < 0) return;
        const next = [...ids];
        const [moved] = next.splice(fromIdx, 1);
        next.splice(toIdx, 0, moved);
        setOrder(next);
        saveOrder(next);
    };

    const reset = () => {
        setOrder([]);
        try { localStorage.removeItem(ORDER_KEY); } catch { /* ignore */ }
    };

    const handlers = (item) => {
        const id = item[idKey];
        return {
            draggable: true,
            onDragStart: (ev) => {
                ev.dataTransfer.effectAllowed = "move";
                ev.dataTransfer.setData("text/plain", id);
                setDragId(id);
            },
            onDragOver: (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = "move"; setOverId(id); },
            onDragLeave: () => { if (overId === id) setOverId(null); },
            onDrop: (ev) => {
                ev.preventDefault();
                const from = ev.dataTransfer.getData("text/plain") || dragId;
                reorder(from, id);
                setDragId(null); setOverId(null);
            },
            onDragEnd: () => { setDragId(null); setOverId(null); },
            "data-dragging": dragId === id ? "true" : undefined,
            "data-dropover": overId === id && dragId && dragId !== id ? "true" : undefined,
        };
    };

    return {
        ordered,
        handlers,
        reset,
        isReordered: order.length > 0,
        dragId, overId,
    };
}
